import { HttpErrorResponse } from '@angular/common/http';

/**
 * Saving a campaign PDF, shared by the guided setup's "Download campaign PDF"
 * and the Detailed tab's "Download campaign results".
 */

/**
 * `attachment; filename="x.pdf"` (or the RFC 5987 `filename*=` form) → `x.pdf`;
 * null without one, or when CORS does not expose the header.
 */
export function dispositionFilename(disposition: string | null): string | null {
  if (!disposition) return null;
  const encoded = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(disposition);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim().replace(/^"|"$/g, ''));
    } catch {
      // fall through to the plain form
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain ? plain[1].trim() : null;
}

/** Hands the file to the browser, then drops the anchor and the object URL. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  // the download has started: the anchor and the object URL can go
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * "First campaign results 2026-10-15.pdf": the campaign's name, what the PDF
 * is (if anything), and the day it was downloaded — readable, unlike the
 * backend's id-based name. Characters no file system takes are dropped;
 * '' when the campaign has no name.
 */
export function readablePdfName(campaignName: string | null | undefined, kind = ''): string {
  const name = (campaignName || '')
    .replace(/[\\/:*?"<>|\u0000-\u001f]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 100)
    .trim();
  if (!name) return '';
  const today = new Date();
  const date = [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, '0'),
    String(today.getDate()).padStart(2, '0'),
  ].join('-');
  return `${[name, kind, date].filter(Boolean).join(' ')}.pdf`;
}

/**
 * The backend's `{ success: false, message }`, out of whatever the request
 * failed with: a JSON body that arrived as a Blob (the response type is
 * blob), or a 200 that turned out to be JSON. '' when there is none — a
 * timeout, a network error, a body that is not JSON.
 */
export async function pdfErrorMessage(error: unknown): Promise<string> {
  const blob =
    error instanceof Blob
      ? error
      : error instanceof HttpErrorResponse && error.error instanceof Blob
        ? error.error
        : null;
  if (!blob) return '';
  try {
    const body = JSON.parse(await blob.text());
    return typeof body?.message === 'string' ? body.message : '';
  } catch {
    return '';
  }
}
